import React, { useContext } from "react";
import { Input } from "reactstrap";
import { UserContext } from "../utils";

// The app-wide active-account control for CUSTOMER users, rendered by DesktopContainer so every screen
// gets it without a per-screen edit (multi-account plan 5.1 / step 7 slice D).
//
// Two shapes, deliberately: more than one active account renders a picker; exactly one renders a
// read-only label. The single-account label is not decoration - before this, a customer had no
// indication anywhere on screen which account they were looking at.
//
// Internal users render NOTHING. Their "active customer" is a browsing convenience chosen from the
// dashboard typeahead, not an authorization scope, and the accounts list is customer-only anyway
// (Customer/MyActiveAccounts is AnyCustomer, so it would 403 and toast for them).
export default function ActiveAccountPicker() {
   const userCtx = useContext(UserContext);
   const accounts = userCtx?.myCustomerAccounts ?? [];

   const sameAccount = (a, b) =>
      !!a && !!b && String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

   if (!userCtx?.currentUser?.isAnyCustomerTypeRole || accounts.length === 0) return null;

   // The account actually in play, falling back to the primary. `accounts` is already the ACTIVE
   // assigned set from the server, so anything not found in it is not selectable - land on the first
   // rather than display a value the server would refuse.
   const selectedValue = userCtx.customerFilter?.value ?? userCtx.currentUser.customerId;
   const current = accounts.find((a) => sameAccount(a.value, selectedValue)) ?? accounts[0];

   function onChange(e) {
      const picked = accounts.find((a) => a.value === e.target.value);
      if (!picked || sameAccount(picked.value, current.value)) return;

      // Persist, then hard-reload. Every customer-reachable screen COPIES the active account into its
      // own local state during an init effect (CustomerDashboard, InvoiceAndPackingList:70-73,
      // OrderPlacement), so nothing reacts to an in-place context change. Reloading re-initializes all
      // of them through the single restore path in App.js/useActiveCustomer that slice B verified,
      // instead of re-opening the dashboard load sequencing that has already caused two prod bugs.
      // Switching account is a full context switch - a cart cannot span accounts - so discarding
      // in-page form state is the correct outcome here, not collateral damage.
      //
      // NOTE: persist() is a deliberate no-op while impersonating (it would overwrite the admin's own
      // saved account), so this control does not switch accounts under impersonation. Verify it with a
      // real customer login instead.
      userCtx.setCustomerFilter(picked);
      userCtx.activeCustomer.persist(picked);
      window.location.reload();
   }

   if (accounts.length === 1) {
      return (
         <span className="text-muted ms-2" title="Your account">
            {current.label}
         </span>
      );
   }

   return (
      <Input
         type="select"
         className="d-inline-block w-auto ms-2"
         bsSize="sm"
         value={current.value}
         onChange={onChange}
         title="Switch account"
         aria-label="Active account"
      >
         {accounts.map((a) => (
            <option key={a.value} value={a.value}>
               {a.label}
            </option>
         ))}
      </Input>
   );
}
